import { createApi } from "@reduxjs/toolkit/dist/query/react";
import { baseQuery } from "@/redux/baseQuery";
import {
  StripeSetupIntentResponse,
  StripeSetupIntentParams,
  StripeCustomerResponse,
  StripePaymentMethodsResponse,
  StripePaymentMethod,
} from "../payment/stripe/types";
import { transformProviderInvoicesToBillingHistory } from "./helpers";
import {
  CreateSubscriptionRequest,
  CreateSubscriptionResponse,
  ChangeSubscriptionPlanRequest,
  ChangeSubscriptionPlanResponse,
  GetSubscriptionPlansResponse,
  GetProviderInvoicesParams,
  GetProviderInvoicesResponse,
  GetProviderSubscriptionsParams,
  GetProviderSubscriptionsResponse,
  SubscriptionAssignment,
  SubscriptionBillingHistoryItem,
} from "./types";

export const subscriptionPlansApi = createApi({
  reducerPath: "subscriptionPlansApi",
  baseQuery,
  tagTypes: ["PaymentMethods", "ProviderSubscriptions", "StripeCustomer"],
  endpoints: (builder) => ({
    getSubscriptionPlans: builder.query<GetSubscriptionPlansResponse, void>({
      query: () => "/v1/subscription-plans",
    }),
    createSubscription: builder.mutation<CreateSubscriptionResponse, { providerId: string } & CreateSubscriptionRequest>({
      query: ({ providerId, ...body }) => ({
        url: `/v4/providers/${providerId}/subscriptions`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["ProviderSubscriptions"],
    }),
    // Operator Subscription Stripe Endpoints
    createSetupIntent: builder.mutation<StripeSetupIntentResponse, { providerId: string } & StripeSetupIntentParams>({
      query: ({ providerId, ...body }) => ({
        url: `/v4/providers/${providerId}/stripe-setup-intent`,
        method: "POST",
        body,
      }),
    }),
    getStripeCustomer: builder.query<StripeCustomerResponse, { providerId: string }>({
      query: ({ providerId }) => `/v4/providers/${providerId}/stripe-customer`,
      providesTags: ["StripeCustomer"],
    }),
    updateStripeCustomer: builder.mutation<StripeCustomerResponse, { providerId: string; email?: string; name?: string }>({
      query: ({ providerId, ...body }) => ({
        url: `/v4/providers/${providerId}/stripe-customer`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["StripeCustomer"],
    }),
    getPaymentMethods: builder.query<StripePaymentMethodsResponse, { customerId: string }>({
      query: ({ customerId }) => `/v1/stripe/customers/${customerId}/payment-methods`,
      transformResponse: (response: {
        data: Array<{
          id: string;
          type: string;
          card: {
            brand: string;
            last4: string;
            exp_month: number;
            exp_year: number;
            funding: string;
          };
          created: number;
        }>;
      }): StripePaymentMethodsResponse =>
        response.data.map((pm) => ({
          paymentMethodId: pm.id,
          type: pm.type,
          card: {
            brand: pm.card.brand,
            last4: pm.card.last4,
            expMonth: pm.card.exp_month,
            expYear: pm.card.exp_year,
            funding: pm.card.funding,
          },
          isDefault: false,
          created: pm.created,
        })),
    }),
    // Provider Payment Methods (v4 endpoints)
    getProviderPaymentMethods: builder.query<StripePaymentMethodsResponse, { providerId: string }>({
      query: ({ providerId }) => `/v4/providers/${providerId}/stripe-payment-methods`,
      providesTags: ["PaymentMethods"],
    }),
    updateProviderPaymentMethod: builder.mutation<
      StripePaymentMethod,
      { providerId: string; paymentMethodId: string; isDefault?: boolean }
    >({
      query: ({ providerId, paymentMethodId, ...body }) => ({
        url: `/v4/providers/${providerId}/stripe-payment-methods/${paymentMethodId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["PaymentMethods"],
    }),
    deleteProviderPaymentMethod: builder.mutation<StripePaymentMethod, { providerId: string; paymentMethodId: string }>({
      query: ({ providerId, paymentMethodId }) => ({
        url: `/v4/providers/${providerId}/stripe-payment-methods/${paymentMethodId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["PaymentMethods"],
    }),
    // Provider Subscription Assignments
    getProviderSubscriptions: builder.query<SubscriptionAssignment[], GetProviderSubscriptionsParams>({
      query: ({ providerId, status, limit, orderBy }) => {
        const params = new URLSearchParams();
        if (status && status.length > 0) {
          status.forEach((s) => params.append("status", s));
        }
        if (limit) {
          params.set("limit", limit.toString());
        }
        if (orderBy && orderBy.length > 0) {
          orderBy.forEach((o) => params.append("orderBy[]", o));
        }
        const queryString = params.toString();
        return `/v4/providers/${providerId}/subscriptions${queryString ? `?${queryString}` : ""}`;
      },
      transformResponse: (response: GetProviderSubscriptionsResponse) => response.data,
      providesTags: ["ProviderSubscriptions"],
    }),
    changeSubscriptionPlan: builder.mutation<
      ChangeSubscriptionPlanResponse,
      { providerId: string; subscriptionAssignmentId: string } & ChangeSubscriptionPlanRequest
    >({
      query: ({ providerId, subscriptionAssignmentId, ...body }) => ({
        url: `/v4/providers/${providerId}/subscriptions/${subscriptionAssignmentId}/plan`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["ProviderSubscriptions"],
    }),
    cancelScheduledPlanChange: builder.mutation<
      { message: string; currentPlan: string },
      { providerId: string; subscriptionAssignmentId: string }
    >({
      query: ({ providerId, subscriptionAssignmentId }) => ({
        url: `/v4/providers/${providerId}/subscriptions/${subscriptionAssignmentId}/scheduled-change`,
        method: "DELETE",
      }),
      invalidatesTags: ["ProviderSubscriptions"],
    }),
    cancelProviderSubscription: builder.mutation<
      { assignment: SubscriptionAssignment },
      { providerId: string; subscriptionAssignmentId: string }
    >({
      query: ({ providerId, subscriptionAssignmentId }) => ({
        url: `/v4/providers/${providerId}/subscriptions/${subscriptionAssignmentId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ProviderSubscriptions"],
    }),
    // Provider Invoices (Billing History)
    getProviderInvoices: builder.query<GetProviderInvoicesResponse, GetProviderInvoicesParams>({
      query: ({ providerId, page = 1, limit = 10, includeVoided = false }) => {
        const params = new URLSearchParams();
        params.set("page", page.toString());
        params.set("limit", limit.toString());
        if (includeVoided) {
          params.set("includeVoided", "true");
        }
        params.append("orderBy[]", "createdAt:DESC");
        return `/v4/providers/${providerId}/invoices?${params.toString()}`;
      },
    }),
    // Billing history with transformation to simplified format
    getProviderBillingHistory: builder.query<SubscriptionBillingHistoryItem[], GetProviderInvoicesParams>({
      query: ({ providerId, page = 1, limit = 10, includeVoided = false }) => {
        const params = new URLSearchParams();
        params.set("page", page.toString());
        params.set("limit", limit.toString());
        if (includeVoided) {
          params.set("includeVoided", "true");
        }
        params.append("orderBy[]", "createdAt:DESC");
        return `/v4/providers/${providerId}/invoices?${params.toString()}`;
      },
      transformResponse: (response: GetProviderInvoicesResponse) => transformProviderInvoicesToBillingHistory(response),
    }),
  }),
});

export const {
  useGetSubscriptionPlansQuery,
  useLazyGetSubscriptionPlansQuery,
  useCreateSubscriptionMutation,
  useCreateSetupIntentMutation,
  useGetStripeCustomerQuery,
  useLazyGetStripeCustomerQuery,
  useUpdateStripeCustomerMutation,
  useGetPaymentMethodsQuery,
  useLazyGetPaymentMethodsQuery,
  useGetProviderPaymentMethodsQuery,
  useLazyGetProviderPaymentMethodsQuery,
  useUpdateProviderPaymentMethodMutation,
  useDeleteProviderPaymentMethodMutation,
  useGetProviderSubscriptionsQuery,
  useLazyGetProviderSubscriptionsQuery,
  useChangeSubscriptionPlanMutation,
  useCancelScheduledPlanChangeMutation,
  useCancelProviderSubscriptionMutation,
  useGetProviderInvoicesQuery,
  useLazyGetProviderInvoicesQuery,
  useGetProviderBillingHistoryQuery,
  useLazyGetProviderBillingHistoryQuery,
} = subscriptionPlansApi;
